/**
 * Aadhaar / PAN / TAN validation and OCR number extraction. Browser-side:
 * Tier 3 values never reach the Worker. Ported from Spike 3.
 *
 * HARD RULE (Spike 3 F3): a checksum may only REJECT a candidate, never CHOOSE
 * one. About 1 in 10 arbitrary 12-digit strings passes Verhoeff, so scanning
 * windows for "the first valid one" returns checksum-valid wrong numbers.
 */
import type { PartyType } from './ast';

/* ------------------------------------------------------------- Verhoeff */

const D: readonly (readonly number[])[] = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 2, 3, 4, 0, 6, 7, 8, 9, 5],
  [2, 3, 4, 0, 1, 7, 8, 9, 5, 6],
  [3, 4, 0, 1, 2, 8, 9, 5, 6, 7],
  [4, 0, 1, 2, 3, 9, 5, 6, 7, 8],
  [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
  [6, 5, 9, 8, 7, 1, 0, 4, 3, 2],
  [7, 6, 5, 9, 8, 2, 1, 0, 4, 3],
  [8, 7, 6, 5, 9, 3, 2, 1, 0, 4],
  [9, 8, 7, 6, 5, 4, 3, 2, 1, 0],
];
const P: readonly (readonly number[])[] = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 5, 7, 6, 2, 8, 3, 0, 9, 4],
  [5, 8, 0, 3, 7, 9, 6, 1, 4, 2],
  [8, 9, 1, 6, 0, 4, 3, 5, 2, 7],
  [9, 4, 5, 3, 1, 2, 6, 8, 7, 0],
  [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
  [2, 7, 9, 3, 8, 0, 6, 4, 1, 5],
  [7, 0, 4, 6, 9, 1, 3, 2, 5, 8],
];

export function verhoeffValid(digits: string): boolean {
  if (!/^\d+$/.test(digits)) return false;
  let c = 0;
  const len = digits.length;
  for (let i = 0; i < len; i++) {
    const digit = Number(digits[len - 1 - i]);
    c = D[c]![P[i % 8]![digit]!]!;
  }
  return c === 0;
}

export type AadhaarResult =
  | { ok: true; digits: string; formatted: string; masked: string; last4: string }
  | { ok: false; reason: 'length' | 'leading_digit' | 'checksum' };

export function validateAadhaar(raw: string): AadhaarResult {
  const digits = raw.replace(/\D/g, '');
  if (digits.length !== 12) return { ok: false, reason: 'length' };
  // UIDAI never issues numbers beginning with 0 or 1.
  if (digits[0] === '0' || digits[0] === '1') return { ok: false, reason: 'leading_digit' };
  if (!verhoeffValid(digits)) return { ok: false, reason: 'checksum' };
  const last4 = digits.slice(8);
  return {
    ok: true,
    digits,
    formatted: `${digits.slice(0, 4)} ${digits.slice(4, 8)} ${last4}`,
    masked: `XXXX XXXX ${last4}`,
    last4,
  };
}

/* ----------------------------------------------------------------- PAN */

/** The 4th PAN character encodes the holder type. */
export const PAN_HOLDER_TYPES = {
  P: 'individual', C: 'company', H: 'huf', F: 'firm_llp', A: 'aop', T: 'trust',
  B: 'body_of_individuals', L: 'local_authority', J: 'artificial_juridical_person', G: 'government',
} as const;
export type PanHolderType = (typeof PAN_HOLDER_TYPES)[keyof typeof PAN_HOLDER_TYPES];

export type PanResult =
  | { ok: true; pan: string; holderType: PanHolderType; partyTypeMismatch: boolean }
  | { ok: false; reason: 'format' | 'holder_type' };

/**
 * `partyTypeMismatch` is advisory: a PAN saying "individual" on a party marked
 * Firm/Company (or the reverse) is almost always a data-entry or OCR error.
 */
export function validatePan(raw: string, holder?: PartyType | 'person' | 'organisation'): PanResult {
  const pan = raw.toUpperCase().replace(/\s/g, '');
  if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan)) return { ok: false, reason: 'format' };
  const code = pan[3] as keyof typeof PAN_HOLDER_TYPES;
  const holderType = PAN_HOLDER_TYPES[code];
  if (!holderType) return { ok: false, reason: 'holder_type' };

  // Built-in party types, or the kind of an admin's own type.
  const kind = holder === 'public' || holder === 'person' ? 'person'
    : holder === 'firm_company' || holder === 'bank' || holder === 'organisation' ? 'organisation' : null;
  let partyTypeMismatch = false;
  if (kind === 'person' && code !== 'P') partyTypeMismatch = true;
  if (kind === 'organisation' && code === 'P') partyTypeMismatch = true;
  return { ok: true, pan, holderType, partyTypeMismatch };
}

/* ----------------------------------------------------------------- TAN */

export function validateTan(raw: string): { ok: true; tan: string } | { ok: false; reason: 'format' } {
  const tan = raw.toUpperCase().replace(/\s/g, '');
  return /^[A-Z]{4}[0-9]{5}[A-Z]$/.test(tan) ? { ok: true, tan } : { ok: false, reason: 'format' };
}

/* --------------------------------------- ID numbers inside typed text */

/** 12 digits (optionally grouped 4-4-4) not starting with 0 or 1; counted only when Verhoeff-valid. */
export const AADHAAR_LIKE = /(?<!\d)([2-9]\d{3})[ -]?(\d{4})[ -]?(\d{4})(?!\d)/g;
export const PAN_LIKE = /(?<![A-Z0-9])[A-Z]{3}[ABCFGHLJPTK][A-Z]\d{4}[A-Z](?![A-Z0-9])/;
export const TAN_LIKE = /(?<![A-Z0-9])[A-Z]{4}\d{5}[A-Z](?![A-Z0-9])/;

/**
 * Which kinds of ID number a piece of typed text seems to contain (templates, lists, review messages):
 * a checksum-valid Aadhaar, a PAN- or a TAN-shaped string (PAN/TAN in any case). These must never be
 * stored on the server (they stay on the device).
 */
export function idNumbersInText(text: string): ('Aadhaar' | 'PAN' | 'TAN')[] {
  const out: ('Aadhaar' | 'PAN' | 'TAN')[] = [];
  for (const m of text.matchAll(AADHAAR_LIKE)) {
    if (validateAadhaar(m[1]! + m[2]! + m[3]!).ok) { out.push('Aadhaar'); break; }
  }
  const upper = text.toUpperCase();
  if (PAN_LIKE.test(upper)) out.push('PAN');
  if (TAN_LIKE.test(upper)) out.push('TAN');
  return out;
}

/* ------------------------------------------------- OCR number extraction */

export interface AadhaarExtraction {
  /** Null when no token has the right shape. Never invented. */
  number: string | null;
  shape: 'grouped-4-4-4' | 'contiguous-12' | null;
  checksumValid: boolean;
  /** True whenever the executive must look closely before accepting. */
  needsReview: boolean;
  /** Other same-shaped tokens found; more than zero means ambiguity. */
  alternatives: number;
}

/**
 * Extract an Aadhaar number from digit-restricted OCR text by token structure.
 * Never slides a window across tokens (see HARD RULE above).
 */
export function extractAadhaarNumber(ocrText: string): AadhaarExtraction {
  type Candidate = { number: string; shape: 'grouped-4-4-4' | 'contiguous-12'; ownLine: boolean };
  const candidates: Candidate[] = [];
  // A candidate must not be glued to a neighbouring digit OR a date separator.
  // "12/08/1985 9999 9999 0018" must not yield "1985 9999 9999" (found porting
  // Spike 3). The lookahead makes matches overlapping, so digits consumed by a
  // bad candidate stay available to the real one.
  const EDGE_BEFORE = String.raw`(?<![\d/.\-])`;
  const EDGE_AFTER = String.raw`(?![\d/.\-])`;
  const grouped = new RegExp(String.raw`${EDGE_BEFORE}(?=(\d{4})[ \t-]+(\d{4})[ \t-]+(\d{4})${EDGE_AFTER})`, 'g');
  const contiguous = new RegExp(String.raw`${EDGE_BEFORE}(?=(\d{12})${EDGE_AFTER})`, 'g');

  // Four or more 4-digit groups in a row are a Virtual ID (16 digits, printed next to the
  // number on newer cards) or digits run together: any three of them would look like an
  // Aadhaar number. Blank them out so they yield no candidate at all.
  const vidRun = new RegExp(String.raw`${EDGE_BEFORE}\d{4}(?:[ \t-]+\d{4}){3,}${EDGE_AFTER}`, 'g');
  ocrText = ocrText.replace(vidRun, run => run.replace(/\d/g, '#'));

  const lines = ocrText.split(/\r?\n/).map(l => l.trim());
  const onOwnLine = (digits: string) => lines.some(l => l.replace(/[ \t-]/g, '') === digits);

  for (const m of ocrText.matchAll(grouped)) {
    const number = `${m[1]}${m[2]}${m[3]}`;
    candidates.push({ number, shape: 'grouped-4-4-4', ownLine: onOwnLine(number) });
  }
  for (const m of ocrText.matchAll(contiguous)) {
    candidates.push({ number: m[1]!, shape: 'contiguous-12', ownLine: onOwnLine(m[1]!) });
  }

  const unique = candidates.filter((c, i) => candidates.findIndex(o => o.number === c.number) === i);
  if (unique.length === 0) {
    return { number: null, shape: null, checksumValid: false, needsReview: true, alternatives: 0 };
  }

  // Checksum and layout FILTER; neither picks among several survivors.
  const valid = unique.filter(c => validateAadhaar(c.number).ok);
  const ownLine = unique.filter(c => c.ownLine);
  const chosen = valid.length === 1 ? valid[0]! : ownLine.length === 1 ? ownLine[0]! : unique[0]!;
  const checksumValid = validateAadhaar(chosen.number).ok;
  return {
    number: chosen.number,
    shape: chosen.shape,
    checksumValid,
    needsReview: !checksumValid || unique.length > 1,
    alternatives: unique.length - 1,
  };
}

export interface PanExtraction {
  /** Null when no single word has the PAN shape. Never invented. */
  pan: string | null;
  /** Letters and digits commonly confused by OCR (0/O, 1/I, 5/S, 8/B, 2/Z) were swapped to fit the shape. */
  corrected: boolean;
  needsReview: boolean;
  /** Other PAN-shaped words found; more than zero means ambiguity. */
  alternatives: number;
}

const TO_LETTER: Record<string, string> = { 0: 'O', 1: 'I', 2: 'Z', 5: 'S', 8: 'B' };
const TO_DIGIT: Record<string, string> = { O: '0', D: '0', Q: '0', I: '1', L: '1', Z: '2', S: '5', B: '8' };

/**
 * Extract a PAN (AAAAA9999A) from OCR text, one whitespace-separated word at a
 * time. Words are never joined (the PAN-card text has many capital words; joining
 * them makes PAN-shaped nonsense). The format check only REJECTS (HARD RULE).
 */
export function extractPanNumber(ocrText: string): PanExtraction {
  const found: { pan: string; corrected: boolean }[] = [];
  for (const raw of ocrText.toUpperCase().split(/\s+/)) {
    const word = raw.replace(/^[^A-Z0-9]+|[^A-Z0-9]+$/g, '');
    if (word.length !== 10 || !/^[A-Z0-9]+$/.test(word)) continue;
    const fixed = [...word].map((ch, i) => (i < 5 || i === 9 ? TO_LETTER[ch] ?? ch : TO_DIGIT[ch] ?? ch)).join('');
    if (validatePan(fixed).ok) found.push({ pan: fixed, corrected: fixed !== word });
  }
  const unique = found.filter((c, i) => found.findIndex(o => o.pan === c.pan) === i);
  if (!unique.length) return { pan: null, corrected: false, needsReview: true, alternatives: 0 };
  const chosen = unique[0]!;
  return { pan: chosen.pan, corrected: chosen.corrected, needsReview: chosen.corrected || unique.length > 1, alternatives: unique.length - 1 };
}
